/* eslint-disable react-hooks-ext/exhaustive-deps -- TODO(react-hooks): 2 hook(s) need dependency review; fix each and remove this directive */
import { useTranslation, untranslated } from "@i18n";
import React, { useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { BiGitCompare } from "react-icons/bi";
import { GiHamburgerMenu } from "react-icons/gi";
import { MdArrowDropDown } from "react-icons/md";
import { RiErrorWarningFill } from "react-icons/ri";
import { useSelector } from "react-redux";
import { useAppDispatch as useDispatch } from "@Reducers/hooks";
import { Box, IconButton } from "@mui/material";
import Tooltip, { tooltipClasses } from "@mui/material/Tooltip";
import { styled } from "@mui/material/styles";
import fileConversionStatusInProgress from "@Assets/icons/svg/FileConversionStatusInProgress.svg?react";
import fileConversionStatusUnsuccessfull from "@Assets/icons/svg/FileConversionStatusUnsuccessfull.svg?react";
import { convertDate } from "@Services/luxonHelper";
import DesignViewerLayoutManager from "@Features/design-viewer/components/DesignViewerLayoutManager";
import { setMinimizerData } from "@Reducers/minimizerFloatter";
import ToolTipComponent from "@CubeComponents/html/tooltip";
import { getViewerUrl, extractRedirectUrl, extractRedirectUrlAsString } from "@Helpers";
import { isDocumentNumberField } from "@Helpers/constants";
import { setViewerState, setModalStatus, setShowOnlyModal } from "@Reducers/Viewer";
import { getFileImage } from "@Services/utils/iconMap";
import { BIM_ENGINE, CAD_ENGINE, PDF_ENGINE } from "@Services/constants";
import useModulesMapper from "@Components/StickySidebarViewer/ModulesMapper.jsx";
import _ from "lodash";
import { getTransmittalAttachedFiles } from "@Features/transmittals";
import { getChildrenFilesName } from "@Actions/Viewer";
import { selectFileAccessFor } from "@Reducers/AccessPermissionReducer";
import { setHelpSectionState } from "@Reducers/portalDashboard";
import IconComponent from "@CubeComponents/icons";
import Text from "@CubeComponents/html/Text/Text";
import ButtonComponent from "@CubeComponents/form/ButtonComponent";
import { BUTTON_TYPES } from "@CubeComponents/constants/globalVariable";
import { RootState } from "@Reducers/index";
import type { SourceFileVersionData } from "@Types/fileOperations";
import type { ObjectIdString } from "@Helpers/schemas/common.schema";
import type { TooltipProps } from "@mui/material/Tooltip";
import type { TranslatedString } from "@i18n";
import type { ModalEntry } from "@Reducers/Viewer";
import type { HoopsViewerHandle } from "@Features/design-viewer/components/DesignViewerLayoutSetting";
import type { SidebarModuleEntry } from "@Features/design-viewer/components/DesignViewerLayoutManager";
import { GuestAccountPanel, SHARE_SIGN_IN_HREF, SHARE_SIGN_UP_HREF } from "@Features/external-share";
import { getShareViewerSession, getShareViewerVersions } from "@Features/external-share/shareViewerSession";

/** One row of the file-version dropdown, as built by DesignViewer's `getFileVersionsDropDownItems`. */
export type FileVersionDropDownItem = {
  text: string;
  url: string;
  createdDate: number;
  versionNumber: number;
  awsFileName: string;
  fileVersionId: string;
};

/** The merged-file conversion banner state, as computed by DesignViewer. */
export type ConstituentFilesProcessingStatus = {
  status: string;
  count?: number;
  message?: string;
  buttonContent?: string;
};

/**
 * A row of the file-name dropdown. Two sources feed it — `getChildrenFilesName` (sibling files
 * under the same parent, which supply `fileVersion`) and `getTransmittalAttachedFiles` (files
 * shared through a transmittal, which supply `fileVersionId`/`toSpace`) — so each source's own
 * members are optional here.
 */
type HeaderFileListItem = Awaited<ReturnType<typeof getChildrenFilesName>>[number] & {
  fileVersionId?: string;
  toSpace?: string;
};

/**
 * `NamedNodeMap` is declared in lib.dom with only a numeric index signature, but the handlers below
 * read the `id` attribute by name off a live element.
 */
type AttributesByName = NamedNodeMap & { id: { value: string } };

interface DesignViewerHeaderProps {
  /** `null` until DesignViewer has resolved whether the route carries a transmittalId. */
  isPageOpenedThroughTransmittal: boolean | null;
  /** Genuinely a union: the transmittal effects seed it with "" before a row supplies the index. */
  currentVersionNumber: number | string;
  fileName: string;
  /** `undefined` when neither dropdown branch applies — the render already optional-chains it. */
  fileVersionsDropDownItems?: FileVersionDropDownItem[];
  handleComparedFileModelChange: (val: string[]) => void;
  handleComparedFileVersionChange: (val: string) => void;
  handleFileVersionIdChange: (val: string) => void;
  /** `null` until the viewer shell has handed its rc-dock handle up to DesignViewer. */
  HoopsViewer: HoopsViewerHandle | null;
  currentfileVersionId: string;
  engineType: string;
  modalToOpen: string[];
  enableDesignViewerLayoutManager?: boolean;
  estimateId?: string | null;
  scheduleId?: string | null;
  /** Forwarded by DesignViewer but not read here. */
  scheduleVersionId?: string | null;
  constituentFilesProcessingStatus: ConstituentFilesProcessingStatus | null;
  resetDesignViewer: () => void;
  setTransmittalId: (id: string) => void;
  /**
   * Recipient share view (CP-283). Suppresses the layout/tool controls (FR-19 permits mouse-driven
   * navigation only) and the app-shell window chrome, which a recipient has nothing to return to.
   * Absent in the authenticated app, so the header is unchanged there.
   */
  isShareView?: boolean;
}

function DesignViewerHeader({
  isPageOpenedThroughTransmittal,
  currentVersionNumber,
  fileName,
  fileVersionsDropDownItems,
  handleComparedFileModelChange,
  handleComparedFileVersionChange,
  handleFileVersionIdChange,
  HoopsViewer,
  currentfileVersionId,
  engineType,
  modalToOpen,
  enableDesignViewerLayoutManager,
  estimateId,
  scheduleId,
  constituentFilesProcessingStatus,
  resetDesignViewer,
  setTransmittalId,
  isShareView = false,
}: DesignViewerHeaderProps) {
  const { t } = useTranslation();
  const projectData = useSelector((state: RootState) => state.projectData);
  const helpSectionState = useSelector((state: RootState) => state.helpSectionState);
  // The single isFile403 result, fetched once by DesignViewer.
  const fileAccess = useSelector((state: RootState) => selectFileAccessFor(state, currentfileVersionId));
  // Select the primitive, not the entry. `state.filesData[id] || {}` allocated a fresh object every
  // time the selector ran, so useSelector's Object.is check never held and this component re-rendered
  // on every dispatched action. `parentId` is a string, compared by value.
  const parentId = useSelector((state: RootState) => state.filesData[currentfileVersionId]?.parentId);
  const layout = useSelector((state: RootState) => state.layout);
  const showDocAndRevisionInViewer = !!layout?.FILE_MANAGER?.SHOW_DOC_AND_REVISION_IN_VIEWER;
  const customFieldValues = useSelector((state: RootState) => state.filesData[currentfileVersionId]?.customFields);
  const fileFormTemplate = useSelector((state: RootState) => state.filesData[currentfileVersionId]?.formTemplate);
  const revisionLabel = useSelector((state: RootState) => state.filesData[currentfileVersionId]?.revisionSeries?.label);
  const documentNumber = React.useMemo(() => {
    if (!showDocAndRevisionInViewer || !customFieldValues) return "";
    const sections = (fileFormTemplate ?? []) as { questionFields?: { _id?: string; questionValue?: string }[] }[];
    for (const section of sections) {
      const question = (section?.questionFields ?? []).find((field) => isDocumentNumberField(field?.questionValue));
      if (question?._id) return String(customFieldValues[question._id] ?? "");
    }
    return "";
  }, [showDocAndRevisionInViewer, customFieldValues, fileFormTemplate]);
  const HoopsModalStatus = useSelector((state: RootState) => state.HoopsModalStatus);
  const fileVersions = useSelector((state: RootState) => state.fileVersions);

  const navigate = useNavigate();
  const MAXIMIZE = "maximize";
  const MINIMIZE = "minimize";
  const DISMISS = "dismiss";
  const ICON_DEFAULT = "ICON_DEFAULT";
  const ICON_ARROW_MAXIMIZE = "ICON_ARROW_MAXIMIZE";
  const ICON_ARROW_MINIMIZE = "ICON_ARROW_MINIMIZE";

  const [openVersionDropdown, setOpenVersionDropdown] = useState(false);
  const [openShareVersionDropdown, setOpenShareVersionDropdown] = useState(false);
  // Static per mount: the viewer page repopulates the holder (and remounts this header, keyed by
  // file id) on every navigation, so reading once at mount is always current.
  const shareVersions = isShareView ? getShareViewerVersions() : [];
  const [FileNameDropdown, setFileNameDropdown] = useState(false);
  const [inputHeading, setInputHeading] = useState("");
  const [showMinimizeIcon, setShowMinimizeIcon] = useState(false);
  const [showDismissIcon, setShowDismissIcon] = useState(false);
  const [childrenFiles, setChildrenFiles] = useState<HeaderFileListItem[]>([]);
  const dispatch = useDispatch();
  const location = useLocation();
  const Url = new URLSearchParams(location.search);
  const spaceId = Url.get("spaceId");
  const [maximizeIconState, setMaximizeIconState] = useState(ICON_DEFAULT);
  const [modulesMapper] = useModulesMapper({
    estimateId: !!Url.get("estimateId"),
    fileVersionId: currentfileVersionId,
  }) as [Record<string, SidebarModuleEntry>];

  useEffect(() => {
    if (isPageOpenedThroughTransmittal) {
      setInputHeading(`TS-${currentVersionNumber}`);
    } else {
      setInputHeading(`V${currentVersionNumber}`);
    }
  }, [isPageOpenedThroughTransmittal, currentVersionNumber]);

  // The selector already scopes this to the file now open.
  const showConstituentWarning = Object.values(fileAccess?.files ?? {}).some(
    (file) => file.isDeleted || !file.canViewFile,
  );

  const handleVersionDropDownClick = () => {
    if (!(estimateId || scheduleId) || [PDF_ENGINE].includes(engineType)) {
      setOpenVersionDropdown(!openVersionDropdown);
    }
  };

  const getResroucetypeOfCurrentFileVersion = () => {
    if (!fileVersions || !currentfileVersionId) return "";
    for (const fileVersionObjs of Object.values(fileVersions)) {
      if (!Array.isArray(fileVersionObjs)) continue;
      for (const fv of fileVersionObjs) {
        const version = fv as SourceFileVersionData & { resourceType?: string };
        if (version?.id === currentfileVersionId) return version?.resourceType || "";
      }
    }
    return "";
  };

  const HtmlTooltip = styled(({ className, ...props }: TooltipProps & { className?: string }) => (
    <Tooltip
      arrow
      {...props}
      slotProps={{
        popper: {
          modifiers: [
            {
              name: "offset",
              options: {
                offset: [0, -7],
              },
            },
          ],
        },
      }}
      classes={{ popper: className }}
    />
  ))(() => ({
    [`& .${tooltipClasses.tooltip}`]: {
      backgroundColor: "#192048",
      color: "#FFF",
      maxWidth: "28.125rem",
      maxHeight: "9.375rem",
      height: "auto",
      width: "auto",
      borderRadius: "0.25rem",
      border: "none",
      position: "relative",
      display: "flex",
      gap: "1rem",
      padding: "1rem 2rem",
      "& p": {
        color: "#FFF",
        fontFamily: '"Source Sans 3"',
        fontSize: "0.875rem",
        fontStyle: "normal",
        fontWeight: 400,
        lineHeight: "normal",
        flexGrow: 1,
        wordBreak: "break-word",
      },
      "& button": {
        color: "#FFF",
        fontFamily: '"Source Sans 3"',
        fontSize: "0.875rem",
        fontStyle: "normal",
        fontWeight: 700,
        lineHeight: "normal",
        whiteSpace: "nowrap",
      },
      "&::before": {
        content: '""',
        position: "absolute",
        top: 0,
        "inset-inline-start": 0,
        "inset-inline-end": 0,
        bottom: 0,
        borderRadius: "inherit",
        pointerEvents: "none",
      },
    },
    [`& .${tooltipClasses.arrow}`]: {
      color: "#192048",
      "&::before": {
        backgroundColor: "#192048",
      },
    },
  }));

  const renderProcessingStatusIcon = () => {
    if (!constituentFilesProcessingStatus || constituentFilesProcessingStatus.status === "COMPLETED") return null;

    const IconSrc =
      constituentFilesProcessingStatus.status === "ERROR"
        ? fileConversionStatusUnsuccessfull
        : fileConversionStatusInProgress;
    const handleButtonClick = () => {
      if (constituentFilesProcessingStatus.status === "PROCESSING") {
        globalThis.location.reload();
      }
      if (constituentFilesProcessingStatus.status === "ERROR") {
        dispatch(
          setModalStatus({
            ...HoopsModalStatus,
            ConstituentFiles: _.get(HoopsModalStatus, "ConstituentFiles.show")
              ? { show: false }
              : {
                  show: true,
                  currentStage: "constituentFiles",
                  defaultStage: "constituentFiles",
                  backStage: "constituentFiles",
                },
          }),
        );
      }
    };
    return (
      <HtmlTooltip
        title={
          <div
            style={{
              display: "flex",
              flexDirection: "row",
              justifyContent: "center",
              alignItems: "center",
              width: "100%",
              height: "100%",
            }}
          >
            <p>{constituentFilesProcessingStatus.message}</p>
            <ButtonComponent buttonType={BUTTON_TYPES.BACKGROUND_BUTTON} onClick={handleButtonClick}>
              <b>{constituentFilesProcessingStatus.buttonContent}</b>
            </ButtonComponent>
          </div>
        }
      >
        <IconButton
          className="px-2 py-0"
          sx={{
            "&:hover": {
              backgroundColor: "rgba(255, 255, 255, 0.3)",
            },
          }}
        >
          <IconSrc />
        </IconButton>
      </HtmlTooltip>
    );
  };

  const handleFileVersionRedirectClick = (e: React.MouseEvent<HTMLElement>) => {
    const fileVersionId = (e.currentTarget.attributes as NamedNodeMap & Record<string, Attr>)["data-fileversionid"]!
      .value;
    // `spaceId` is a URL query param (`string | null`) and `getViewerUrl` returns `undefined` when
    // `projectId` is falsy; both are consumed unguarded here. Erased assertions only.
    const spaceId1 = spaceId as string;
    const currentMode = new URLSearchParams(globalThis.location.search).get("mode");
    const viewerUrl = getViewerUrl({
      fileVersionId,
      spaceId: spaceId1,
      projectId: projectData._id,
      mode: currentMode === "scs" || currentMode === "skipProperties" ? currentMode : undefined,
      redirectTo: extractRedirectUrl(location),
    }) as string;
    resetDesignViewer();
    navigate(viewerUrl, { replace: true });
    setOpenVersionDropdown(false);
    handleFileVersionIdChange(fileVersionId);
  };

  const handleTransmittalFileVersionRedirectClick = (e: React.MouseEvent<HTMLElement>, url: string) => {
    const fileVersionId = (e.currentTarget.attributes as NamedNodeMap & Record<string, Attr>)["data-fileversionid"]!
      .value;
    const transmittalIdMatch = url.match(/[?&]transmittalId=([^&]*)/);
    const transmittalId = transmittalIdMatch ? decodeURIComponent(transmittalIdMatch[1]!) : null;
    handleFileVersionIdChange(fileVersionId);
    // KNOWN BUG, preserved verbatim: `transmittalId` is `null` when the url carries no
    // transmittalId, yet DesignViewer's setter is a `useState<string>` setter. Erased assertion.
    setTransmittalId(transmittalId!);
    resetDesignViewer();
    navigate(url, { replace: true });
    setOpenVersionDropdown(false);
  };

  const handleFileVersionCompareClick = (val: FileVersionDropDownItem) => {
    setOpenVersionDropdown(false);
    if ([PDF_ENGINE].includes(engineType)) {
      dispatch(
        setViewerState({
          pdfCompare: {
            active: true,
            baseFileVersionId: currentfileVersionId,
            revisionFileVersionId: val.fileVersionId,
          },
        }),
      );
      return;
    }
    handleComparedFileModelChange(val.awsFileName.split(","));
    handleComparedFileVersionChange(val.fileVersionId);
    dispatch(setShowOnlyModal("ModelCompare"));
  };

  const navigateToRedirectOrBack = () => {
    const redirectUrl = extractRedirectUrlAsString(location);
    redirectUrl ? navigate(redirectUrl) : navigate(-1);
  };

  const cleanupViewer = () => {
    if (document.fullscreenElement) {
      document.exitFullscreen();
    }
    resetDesignViewer();
  };

  const executeMinimizeActions = () => {
    navigateToRedirectOrBack();
    dispatch(
      setMinimizerData({
        path: location.pathname + location.search,
        text: fileName,
      }),
    );
  };

  const executeCloseActions = () => {
    if (globalThis.opener) {
      globalThis.close();
    } else {
      navigateToRedirectOrBack();
    }
  };

  const handleMinimizeViewer = () => {
    cleanupViewer();
    executeMinimizeActions();
  };

  const handleCloseViewer = () => {
    cleanupViewer();
    executeCloseActions();
  };

  const handleFullScreenViewer = () => {
    if (document.fullscreenElement) {
      document.exitFullscreen();
    } else if (
      document.documentElement.requestFullscreen &&
      typeof document.documentElement.requestFullscreen === "function"
    ) {
      document.documentElement.requestFullscreen();
    }
  };

  const handleClickFileListDropDown = () => {
    if (!(estimateId || scheduleId)) {
      setFileNameDropdown(!FileNameDropdown);
    }
  };

  const getFiles = async () => {
    if (isPageOpenedThroughTransmittal) {
      // External entry point: both ids are URL query params, and the transmittal path is only
      // reachable with them present. Each is adopted once here, at its declaration (the backend
      // route schema validates them).
      const transmittalId = Url.get("transmittalId") as ObjectIdString;
      const transmittalSpaceId = spaceId as ObjectIdString;
      const allTransmittalFiles = await getTransmittalAttachedFiles({
        transmittalId,
        spaceId: transmittalSpaceId,
      });
      setChildrenFiles(allTransmittalFiles.data as HeaderFileListItem[]);
    } else {
      const shortcutParentId = Url.get("shortcutParentId");
      const finalParentId = shortcutParentId || parentId;
      if (finalParentId) {
        try {
          const allFiles = await getChildrenFilesName({ finalParentId, spaceId: spaceId! });
          setChildrenFiles(
            allFiles.filter(
              (file: HeaderFileListItem) =>
                (
                  ["RESOURCE_TYPE_MERGED_FILE", "RESOURCE_TYPE_SHORTCUT_FILE", "RESOURCE_TYPE_FILE"] as (
                    | string
                    | undefined
                  )[]
                ).includes(file.resourceType) && file.permission?.read,
            ),
          );
        } catch (err) {
          Promise.reject(err);
          setChildrenFiles([]);
        }
      }
    }
  };

  useEffect(() => {
    if (parentId && spaceId) {
      getFiles();
    }
  }, [parentId, spaceId, location.search]);

  const handleClickOutside = (event: MouseEvent) => {
    const target = event.target as HTMLElement;
    if (
      !(
        target.closest("#filesListDropDown") ||
        target.closest("#versionsListDropDown") ||
        target.closest("#layoutDropDown")
      )
    ) {
      setFileNameDropdown(false);
      setOpenVersionDropdown(false);
    }
  };

  useEffect(() => {
    document.addEventListener("click", handleClickOutside);
    return () => {
      document.removeEventListener("click", handleClickOutside);
    };
  }, []);

  const handleIconsMouseOver = (e: React.MouseEvent<HTMLElement>) => {
    const type = (e.currentTarget.attributes as AttributesByName).id.value;

    if (type == MAXIMIZE) {
      if (document.fullscreenElement) {
        setMaximizeIconState(ICON_ARROW_MINIMIZE);
      } else {
        setMaximizeIconState(ICON_ARROW_MAXIMIZE);
      }
    } else if (type == MINIMIZE) {
      setShowMinimizeIcon(true);
    } else if (type == DISMISS) {
      setShowDismissIcon(true);
    }
  };

  useEffect(() => {
    if (PDF_ENGINE.includes(engineType)) {
      const modalStatus: Record<string, ModalEntry> = {};
      dispatch(setViewerState({ sidebarData: ["Issues", "Properties"] }));
      if (modalToOpen.length > 0) {
        modalToOpen = modalToOpen.filter((data: string) => modulesMapper[data]?.isVisible);
        for (const dock of modalToOpen) {
          const initializeState = modulesMapper[dock]?.initialState || {};
          modalStatus[dock] = {
            show: true,
            ...initializeState,
          };

          if (dock === "Issues") {
            modalStatus[dock]!.currentStage = "view_issue";
          }
        }
      }
      dispatch(setModalStatus(modalStatus));
    }
  }, [engineType]);

  const handleIconsMouseOut = (e: React.MouseEvent<HTMLElement>) => {
    const type = (e.currentTarget.attributes as AttributesByName).id.value;

    if (type == MAXIMIZE) {
      setMaximizeIconState(ICON_DEFAULT);
    } else if (type == MINIMIZE) {
      setShowMinimizeIcon(false);
    } else if (type == DISMISS) {
      setShowDismissIcon(false);
    }
  };

  const handleHelpSectionClick = () => {
    dispatch(setHelpSectionState({ display: !helpSectionState.display, module: "general" }));
  };

  const handleChangeTransmittalFile = (transmittalFileObj: HeaderFileListItem) => {
    // Same two unguarded values as `handleFileVersionRedirectClick`: a `string | null` query param
    // and `getViewerUrl`'s `string | undefined`. Erased assertions only.
    const transmittalVersionId = Url.get("transmittalId") as string;
    const transmittalFileLink = getViewerUrl({
      fileVersionId: transmittalFileObj.fileVersionId,
      spaceId: transmittalFileObj.toSpace,
      transmittalVersionId,
      projectId: projectData.id,
      redirectTo: extractRedirectUrl(location),
    }) as string;
    resetDesignViewer();
    navigate(transmittalFileLink, { replace: true });
    setFileNameDropdown(false);
  };

  const handleChangeModel = (fileVersionId: string | undefined) => {
    if (currentfileVersionId != fileVersionId) {
      const spaceId1 = spaceId as string;
      const viewerUrl = getViewerUrl({
        fileVersionId,
        spaceId: spaceId1,
        projectId: projectData._id,
        redirectTo: extractRedirectUrl(location),
      });
      resetDesignViewer();
      navigate(viewerUrl as string, { replace: true });
      setFileNameDropdown(false);
    }
  };

  return (
    <div className="z-20" data-testid="designViewerHeader">
      <div
        className="flex items-center rounded-lg bg-[linear-gradient(90.03deg,rgba(255,255,255,0.8)_0%,rgba(255,255,255,0.4)_100%)] py-2 ps-6 text-[#333]"
        data-testid="fileInfoSection"
      >
        <div className="w-14">
          <GiHamburgerMenu size="1.2rem" color="#555555" />
        </div>
        <div className="grid grid-flow-col auto-cols-max items-center" data-testid="leftInfoSection">
          <Box
            data-testid="fileListDropdown"
            onClick={handleClickFileListDropDown}
            className={`flex pe-2  border-e border-black ${
              estimateId || scheduleId ? "opacity-50 cursor-not-allowed" : "cursor-pointer"
            }`}
            id="filesListDropDown"
          >
            <ToolTipComponent
              position="right"
              // File and folder names are user data, not locale keys, so they are asserted here
              // rather than translated.
              content={fileName as TranslatedString}
            >
              <div data-testid="fileName" className="max-w-72  truncate text-base font-semibold">
                {showDocAndRevisionInViewer ? documentNumber || fileName : fileName}
              </div>
            </ToolTipComponent>
            {showConstituentWarning && (
              <ToolTipComponent position="bottom" content={t("Some files in this model are missing or restricted.")}>
                <span className="flex items-center px-1" data-testid="constituentWarningIcon">
                  <RiErrorWarningFill className="text-red-500" size={18} />
                </span>
              </ToolTipComponent>
            )}
            {renderProcessingStatusIcon()}
            <MdArrowDropDown size="1.5rem" />
          </Box>

          {showDocAndRevisionInViewer && (
            <Box className="flex px-2 border-e border-black" data-testid="viewerRevision">
              <ToolTipComponent position="bottom" content={t("Revision")}>
                <div className="truncate text-base font-semibold">{revisionLabel || "-"}</div>
              </ToolTipComponent>
            </Box>
          )}

          {/* Recipient version switcher (FR-18/FR-40: the latest version is the DEFAULT selection,
              not a restriction). Fed from the share session's prefetched list — getSharedFile returns
              the full history — and switching navigates between /share viewer routes, so no
              authenticated call is involved. */}
          {isShareView && shareVersions.length > 0 && (
            <div className="relative flex items-center border-e border-black pe-2 ps-3">
              <button
                type="button"
                onClick={() => setOpenShareVersionDropdown((v) => !v)}
                className="flex items-center gap-1 text-base font-semibold"
                data-testid="shareVersionDropdown"
              >
                {untranslated(shareVersions.find((v) => v.versionId === currentfileVersionId)?.label ?? "")}
                <MdArrowDropDown size="1.5rem" />
              </button>
              {openShareVersionDropdown && (
                <div
                  className="absolute start-0 z-20 overflow-auto rounded-lg border border-stroke_primary bg-white shadow-md"
                  style={{ top: "3rem", maxHeight: "20rem", minWidth: "13rem" }}
                >
                  {shareVersions.map((version) => (
                    <button
                      key={version.versionId}
                      type="button"
                      disabled={version.versionId === currentfileVersionId}
                      onClick={() => {
                        setOpenShareVersionDropdown(false);
                        const session = getShareViewerSession();
                        if (session) navigate(`/share/${session.token}/file/${version.versionId}`);
                      }}
                      className={`flex w-full flex-col items-start gap-0.5 border-b border-stroke_primary px-3 py-2 text-start last:border-b-0 ${
                        version.versionId === currentfileVersionId
                          ? "bg-selection_primary"
                          : "hover:bg-card_hover_color"
                      }`}
                    >
                      <span className="text-xs font-semibold">{untranslated(version.label)}</span>
                      {typeof version.createdAt === "number" && (
                        <span className="text-[0.65rem] text-text_light">
                          {untranslated(convertDate(version.createdAt, "dateForText"))}
                        </span>
                      )}
                    </button>
                  ))}
                </div>
              )}
            </div>
          )}
          {!isShareView && (
            <Box
              data-testid="fileVersionDropdown"
              onClick={handleVersionDropDownClick}
              className={`flex pe-2 ps-3 border-e border-black ${
                (estimateId || scheduleId) && ![PDF_ENGINE].includes(engineType)
                  ? "opacity-50 cursor-not-allowed"
                  : "cursor-pointer"
              }`}
              id="versionsListDropDown"
            >
              <div data-testid="inputHeading" className="text-base font-semibold">
                {inputHeading}
              </div>
              <MdArrowDropDown size="1.5rem" />
              {openVersionDropdown && (
                <div
                  data-testid="fileVersionPopOver"
                  className="absolute overflow-auto -ms-3 border border-white bg-white rounded-lg shadow-md"
                  style={{ top: "4.25rem", maxHeight: "34.375rem" }}
                >
                  <div style={{ height: "100%", overflow: "auto" }} data-testid="fileVersionDropdownItems">
                    {fileVersionsDropDownItems?.map((val: FileVersionDropDownItem) => {
                      const formattedTime = convertDate(Number(val.createdDate), "dateForText");
                      const formattedTimeForToolTip = convertDate(Number(val.createdDate), "dateForTooltip");
                      let isDisabled = false;
                      if (currentVersionNumber == val.versionNumber) {
                        isDisabled = true;
                      }
                      return (
                        <div
                          key={`dropDownVersion_${val.text}`}
                          className="grid grid-cols-[auto_auto] gap-x-4 cursor-pointer px-4 py-2.5"
                        >
                          <Box
                            data-fileversionid={val.fileVersionId}
                            onClick={(e) =>
                              isPageOpenedThroughTransmittal
                                ? handleTransmittalFileVersionRedirectClick(e, val.url)
                                : handleFileVersionRedirectClick(e)
                            }
                            className={`grid w-32 grid-cols-[auto_auto] text-sm font-normal ${isDisabled || estimateId || scheduleId ? "pointer-events-none opacity-50" : ""}`}
                          >
                            <span>{val.text}</span>
                            <span className="ps-4 text-[#979797]">
                              <ToolTipComponent showTipPointer position="bottom" content={formattedTimeForToolTip}>
                                {formattedTime}
                              </ToolTipComponent>
                            </span>
                          </Box>
                          {(([BIM_ENGINE, CAD_ENGINE].includes(engineType) && isDisabled === false) ||
                            [PDF_ENGINE].includes(engineType)) &&
                          getResroucetypeOfCurrentFileVersion() != "RESOURCE_TYPE_MERGED_FILE" ? (
                            <ToolTipComponent position="left" content={t("Compare")}>
                              <Box
                                className="flex cursor-pointer items-center text-accentBlue"
                                onClick={() => handleFileVersionCompareClick(val)}
                              >
                                <BiGitCompare />
                              </Box>
                            </ToolTipComponent>
                          ) : null}
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
            </Box>
          )}
          {/* Hidden for a recipient: FR-19 allows mouse-driven navigation only, no on-screen control
              buttons, and the layout manager is exactly those (panels, measure, markup, cutting). */}
          {!isShareView &&
            [BIM_ENGINE, CAD_ENGINE].includes(engineType) &&
            enableDesignViewerLayoutManager &&
            layout.DESIGNVIEWER && (
              <DesignViewerLayoutManager
                // Only rendered once the shell has reported its rc-dock handle up; erased assertion.
                HoopsViewer={HoopsViewer!}
                modalToOpen={modalToOpen}
                fileVersionId={currentfileVersionId}
              />
            )}
        </div>
        {/* Internal window chrome — help centre, minimise/maximise into the app shell, dismiss back to
            the file manager. None of it applies to a recipient, who has no app shell to return to. */}
        <div className="ms-auto flex items-center gap-4 pe-4" data-testid="rightInfoSection">
          {/* A recipient gets the guest panel in place of all of the above (PRD 4.6): it is the only
              thing in this corner that means anything to someone with no CUBE account, and PRD requires
              it on every recipient screen — the viewer included, which is why it lives here rather
              than only in SharedLinkShell. */}
          {isShareView && <GuestAccountPanel signInHref={SHARE_SIGN_IN_HREF} signUpHref={SHARE_SIGN_UP_HREF} />}
          {!isShareView && (
            <>
              <IconComponent
                onClick={handleHelpSectionClick}
                position={"left"}
                toolTipContent={t("Help Section")}
                showGradient={(helpSectionState.display && helpSectionState.module == "general") ?? undefined}
                name="help"
                width="1.5rem"
                height="1.5rem"
              />
              <Box
                onMouseEnter={handleIconsMouseOver}
                onMouseLeave={handleIconsMouseOut}
                className="h-4 w-4 cursor-pointer bg-alice_blue hover:bg-bright_yellow rounded-full flex items-center justify-center transition-colors duration-100"
                id={MINIMIZE}
              >
                <IconComponent
                  key={MINIMIZE}
                  name={"minimizeIcon"}
                  width="0.5rem"
                  height="0.5rem"
                  toolTipContent={t("Minimize")}
                  position="top"
                  onClick={handleMinimizeViewer}
                  data-testid="minimise"
                  parentContainerClass="h-2.5 w-2.5"
                  isVisible={showMinimizeIcon}
                />
              </Box>
              <Box
                onMouseEnter={handleIconsMouseOver}
                onMouseLeave={handleIconsMouseOut}
                className="h-4 w-4 cursor-pointer bg-alice_blue hover:bg-green-600 rounded-full flex items-center justify-center transition-colors duration-100"
                id={MAXIMIZE}
              >
                <IconComponent
                  key={MAXIMIZE}
                  name={maximizeIconState == ICON_ARROW_MINIMIZE ? "exitFullScreen" : "enterFullScreen"}
                  toolTipContent={maximizeIconState == ICON_ARROW_MINIMIZE ? t("Exit Full Screen") : t("Maximise")}
                  position="top"
                  onClick={handleFullScreenViewer}
                  data-testid="maximise"
                  parentContainerClass="h-3 w-3"
                  isVisible={maximizeIconState !== ICON_DEFAULT}
                  width="1.5rem"
                  height="1.5rem"
                />
              </Box>
              <Box
                onMouseEnter={handleIconsMouseOver}
                onMouseLeave={handleIconsMouseOut}
                className="h-4 w-4 cursor-pointer bg-alice_blue hover:bg-red-600 rounded-full flex items-center justify-center transition-colors duration-100"
                id={DISMISS}
              >
                <IconComponent
                  key={DISMISS}
                  name={"Cancel"}
                  width="0.75rem"
                  height="1rem"
                  toolTipContent={t("Dismiss")}
                  position="top"
                  onClick={handleCloseViewer}
                  data-testid="dismiss"
                  parentContainerClass="h-4 w-3"
                  isVisible={showDismissIcon}
                />
              </Box>
            </>
          )}
        </div>
      </div>
      {FileNameDropdown && (
        <div
          className="absolute overflow-auto border border-white bg-white rounded-lg shadow-md"
          style={{ top: "4.25rem", maxHeight: "75%", insetInlineStart: "6.5rem" }}
          data-testid="filesListData"
        >
          <div className="h-full overflow-auto w-72" data-testid="filesListDataContent">
            {childrenFiles.length ? (
              childrenFiles.map((val: HeaderFileListItem) => (
                <ToolTipComponent key={val.id} position="right" content={val.name as TranslatedString}>
                  <Box
                    onClick={() =>
                      isPageOpenedThroughTransmittal
                        ? handleChangeTransmittalFile(val)
                        : handleChangeModel(val.fileVersion)
                    }
                    className={`flex max-w-72 whitespace-nowrap items-center p-3 gap-2 ${currentfileVersionId == val.fileVersion ? "cursor-not-allowed" : "cursor-pointer"} hover:bg-primary_blue`}
                  >
                    <div>
                      <img src={getFileImage(val) as string} className="max-w-none" alt="file-icon" />
                    </div>
                    <div key={val.id} className="truncate " data-testid="filesListName">
                      {val.name}
                    </div>
                  </Box>
                </ToolTipComponent>
              ))
            ) : (
              <div
                className="grid h-[3vh] w-[25vw] items-center ps-1 italic text-[#979797]"
                data-testid="noFilesToShow"
              >
                <Text>{t("No files to Show")}</Text>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

export default DesignViewerHeader;
